import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { STATE_NAMES } from './us-states';

/**
 * What a state says about itself (spec 0012), from GET /api/terminology/:stateCode: its appeal
 * labels, its hearing body, which value types and value statuses it offers, and whether its
 * assessors use an equalization factor or a township multiplier.
 *
 * NULL IS NEVER A RESTRICTION. A null allowlist offers everything and a null applicability flag
 * means "unknown, treated as applicable", so a state nobody configured behaves exactly as it
 * did before. The filters below also refuse to produce an EMPTY picker: an allowlist that
 * matches nothing on offer falls back to the full list (and says so in the console), because a
 * picker with no choices strands the operator worse than one with too many.
 */
export interface StateTerminology {
  state_code: string | null;
  appeal_labels: { informal_hearing: string; formal_hearing: string; arbitration: string; litigation: string };
  hearing_body_label?: string | null;
  allowed_value_types?: string[] | null;
  allowed_value_statuses?: string[] | null;
  uses_equalization_factor?: boolean | null;
  uses_township_multiplier?: boolean | null;
  is_fallback?: boolean;
}

/** A parcel or assessor state as a postal code ("TX" or "Texas" → "TX"), or null. */
export function stateCodeOf(input: string | null | undefined): string | null {
  if (!input) return null;
  const raw = input.trim();
  if (!raw) return null;
  if (STATE_NAMES[raw.toUpperCase()]) return raw.toUpperCase();
  const hit = Object.entries(STATE_NAMES).find(([, name]) => name.toLowerCase() === raw.toLowerCase());
  return hit ? hit[0] : null;
}

function asTerminology(data: unknown): StateTerminology | null {
  return data && typeof data === 'object' && !Array.isArray(data) && 'appeal_labels' in (data as object)
    ? (data as StateTerminology)
    : null;
}

/** The terminology for a state, cached per code. `data` is null while loading or for no state. */
export function useStateTerminology(state: string | null | undefined, enabled = true) {
  const code = stateCodeOf(state);
  const query = useQuery({
    queryKey: ['state-terminology', code],
    queryFn: ({ signal }) => api.get<StateTerminology>(`/api/terminology/${code}`, signal),
    enabled: enabled && !!code,
    staleTime: 5 * 60_000,
  });
  return { ...query, data: asTerminology(query.data) };
}

/** The parcel's terminology for a subtree (the parcel page's value boxes). */
export const StateTerminologyContext = createContext<StateTerminology | null>(null);
export const useTerminologyContext = () => useContext(StateTerminologyContext);

/**
 * Map a stage-template FORUM name to the parcel_values.value_type slot it occupies (still NOT
 * NULL + UNIQUE(parcel_id, tax_year, value_type)). The forum/event labels are the semantic
 * descriptors; value_type is the structural slot, so `ARB`, `BOE` and `Board of Review` all land
 * on `formal_hearing`.
 */
export function forumToValueType(forum: string): string {
  const f = forum.toLowerCase();
  if (f.includes('notice')) return 'proposed';
  if (f.includes('final')) return 'final';
  if (f.includes('arbitration')) return 'arbitration';
  if (f.includes('court') || f.includes('ptab') || f.includes('ptaab')) return 'litigation';
  if (f.includes('boe') || f.includes('board of review') || f.includes('arb')) return 'formal_hearing';
  if (f.includes('settlement') || f.includes('informal') || f.includes('assessor') || f.includes('supervisor')) return 'informal_hearing';
  return 'other';
}

/** The forums whose value-type slot the state allows. Null allowlist → all; no match → all. */
export function filterForums<T extends { name: string }>(forums: T[], allowed: string[] | null | undefined): T[] {
  if (!allowed) return forums;
  const kept = forums.filter((f) => allowed.includes(forumToValueType(f.name)));
  if (forums.length && !kept.length) {
    console.warn('[state terminology] no forum matches the state value-type allowlist; offering all', allowed);
    return forums;
  }
  return kept;
}

/**
 * Status options narrowed to the state's allowlist. The '' placeholder stays; a stored status
 * outside the list is kept (marked "not used in <state>") so the row still shows what it holds
 * and saving another field never changes it.
 */
export function filterStatusOptions(
  options: Array<{ value: string; label: string }>,
  allowed: string[] | null | undefined,
  current: unknown,
  stateLabel: string,
): Array<{ value: string; label: string }> {
  if (!allowed) return options;
  const kept = options.filter((o) => o.value === '' || allowed.includes(o.value));
  if (options.some((o) => o.value !== '') && !kept.some((o) => o.value !== '')) {
    console.warn('[state terminology] no status matches the state allowlist; offering all', allowed);
    return options;
  }
  const cur = current == null ? '' : String(current);
  if (cur && !kept.some((o) => o.value === cur)) {
    const original = options.find((o) => o.value === cur);
    kept.push({ value: cur, label: `${original?.label ?? cur} (not used in ${stateLabel})` });
  }
  return kept;
}

/** The assessor fields a state marks not applicable → the reason shown beside "N/A". */
export function notApplicableFields(term: StateTerminology | null, stateName: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!term) return out;
  const reason = `Not used in ${stateName}`;
  if (term.uses_equalization_factor === false) {
    out.equalization_factor = reason;
    out.equalization_year = reason;
  }
  if (term.uses_township_multiplier === false) {
    out.township_multiplier = reason;
    out.township_multiplier_year = reason;
  }
  return out;
}

/** A state's display name for messages ("MD" → "Maryland"). */
export function stateNameOf(input: string | null | undefined): string {
  const code = stateCodeOf(input);
  return (code && STATE_NAMES[code]) || input || 'this state';
}
